import React, { createContext, useContext, useState, useCallback } from 'react';
import { getContentById, type ContentItem } from '../services/contentService';
import { getContentLikes, getUserLike, toggleLike } from '../services/likesService';
import { getUserProfile, type UserProfile } from '../services/userService';
import { getSubscriptionCount, isSubscribed, subscribeToCreator, unsubscribeFromCreator } from '../services/subscriptionsService';
// import { recordView } from '../services/analyticsService';
import { shareContent } from '../services/shareService';

interface ContentState {
  content: ContentItem | null;
  loading: boolean;
  error: string | null;
  likeCounts: { likes: number; dislikes: number } | null;
  userRating: 'like' | 'dislike' | null;
  creatorProfile: UserProfile | null;
  subscriberCount: number;
  isSubscribedToCreator: boolean;
}

interface ContentActions {
  fetchContent: (contentId: string, userId?: string) => Promise<void>;
  handleLike: (contentId: string, userId: string) => Promise<void>;
  handleDislike: (contentId: string, userId: string) => Promise<void>;
  handleSubscribe: (creatorId: string, userId: string) => Promise<void>;
  handleShare: (contentId: string, userId?: string) => Promise<void>;
  recordContentView: (contentId: string, userId: string) => Promise<void>;
  clearContent: () => void;
}

interface ContentContextValue extends ContentState, ContentActions {}

const ContentContext = createContext<ContentContextValue | undefined>(undefined);

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [likeCounts, setLikeCounts] = useState<{ likes: number; dislikes: number } | null>(null);
  const [userRating, setUserRating] = useState<'like' | 'dislike' | null>(null);
  const [creatorProfile, setCreatorProfile] = useState<UserProfile | null>(null);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [isSubscribedToCreator, setIsSubscribedToCreator] = useState(false);

  const fetchContent = useCallback(async (contentId: string, userId?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const contentData = await getContentById(contentId);
      if (!contentData) {
        setError('Content not found');
        return;
      }

      setContent(contentData);

      // Record view (non-blocking)
      if (userId) {
        try {
          // await recordView(userId, contentId); // Commented out - function not available
        } catch (viewError) {
          // Silently fail - view recording is non-critical
        }
      }

      // Fetch like counts (non-blocking)
      try {
        const likes = await getContentLikes(contentId);
        setLikeCounts(likes);
      } catch (likeError) {
        // Silently fail - like counts are non-critical
      }

      // Fetch user's like status (non-blocking)
      if (userId) {
        try {
          const userLike = await getUserLike(contentId, userId);
          setUserRating(userLike);
        } catch (userLikeError) {
          // Silently fail - user like status is non-critical
        }
      }

      // Fetch creator profile (non-blocking)
      if (contentData.uploaderId) {
        try {
          const profile = await getUserProfile(contentData.uploaderId);
          setCreatorProfile(profile);
        } catch (profileError) {
          // Silently fail - creator profile is non-critical
        }

        // Fetch subscriber count (non-blocking)
        try {
          const subCount = await getSubscriptionCount(contentData.uploaderId);
          setSubscriberCount(subCount);
        } catch (subError) {
          // Silently fail - subscriber count is non-critical
        }

        // Check if current user is subscribed (non-blocking)
        if (userId && userId !== contentData.uploaderId) {
          try {
            const subscribed = await isSubscribed(userId, contentData.uploaderId);
            setIsSubscribedToCreator(subscribed);
          } catch (subError) {
            // Silently fail - subscription status is non-critical
          }
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setError('Content not found or no longer available');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLike = useCallback(async (contentId: string, userId: string) => {
    try {
      const newLikeType = userRating === 'like' ? 'dislike' : 'like';
      const result = await toggleLike(contentId, userId, newLikeType);
      
      setUserRating(result.action === 'removed' ? null : result.newType!);
      
      const updatedCounts = await getContentLikes(contentId);
      setLikeCounts(updatedCounts);
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  }, [userRating]);

  const handleDislike = useCallback(async (contentId: string, userId: string) => {
    try {
      const newLikeType = userRating === 'dislike' ? 'like' : 'dislike';
      const result = await toggleLike(contentId, userId, newLikeType);
      
      setUserRating(result.action === 'removed' ? null : result.newType!);
      
      const updatedCounts = await getContentLikes(contentId);
      setLikeCounts(updatedCounts);
    } catch (error) {
      console.error('Error toggling dislike:', error);
      throw error;
    }
  }, [userRating]);

  const handleSubscribe = useCallback(async (creatorId: string, userId: string) => {
    try {
      if (isSubscribedToCreator) {
        await unsubscribeFromCreator(userId, creatorId);
      } else {
        await subscribeToCreator(userId, creatorId);
      }
      
      setIsSubscribedToCreator(!isSubscribedToCreator);
      
      const newCount = await getSubscriptionCount(creatorId);
      setSubscriberCount(newCount);
    } catch (error) {
      console.error('Subscription error:', error);
      throw error;
    }
  }, [isSubscribedToCreator]);

  const handleShare = useCallback(async (contentId: string, userId?: string) => {
    try {
      if (!content) return;

      const shareUrl = `${window.location.origin}/content/${contentId}`;
      await navigator.clipboard.writeText(shareUrl);
      
      if (userId) {
        try {
          await shareContent(userId, contentId, content.category, 'link', 'web');
        } catch (firebaseError) {
          // Silently fail - share recording is non-critical
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      throw error;
    }
  }, [content]);

  const recordContentView = useCallback(async (contentId: string, userId: string) => {
    try {
      // await recordView(userId, contentId); // Commented out - function not available
    } catch (error) {
      // Silently fail - view recording is non-critical
    }
  }, []);

  const clearContent = useCallback(() => {
    setContent(null);
    setError(null);
    setLikeCounts(null);
    setUserRating(null);
    setCreatorProfile(null);
    setSubscriberCount(0);
    setIsSubscribedToCreator(false);
  }, []);

  const value: ContentContextValue = {
    content,
    loading,
    error,
    likeCounts,
    userRating,
    creatorProfile,
    subscriberCount,
    isSubscribedToCreator,
    fetchContent,
    handleLike,
    handleDislike,
    handleSubscribe,
    handleShare,
    recordContentView,
    clearContent
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = (): ContentContextValue => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within ContentProvider');
  }
  return context;
};
